/**
 * TenantIndicator Component — Current Tenant Badge
 *
 * Displays badge showing currently selected tenant or "Visão Global"
 * Appears in header next to TenantSwitcher dropdown
 *
 * Features:
 * - Updates automatically when TenantContext changes
 * - Shows tenant name for all authenticated users (via GET /tenants/current)
 * - Shows "Visão Global" for superadmin global view
 * - Handles loading and error states gracefully
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Tag, Tooltip } from 'antd';
import { GlobalOutlined, TeamOutlined } from '@ant-design/icons';
import { useTenantContext } from '@/context/TenantContext';
import { useAuth } from '@/context/AuthContext';
import Api from '@/services/api';
import type { Tenant } from '@/types/tenant';

/**
 * TenantIndicator badge component
 * 
 * Displays:
 * - Superadmin in global view: "Visão Global" badge
 * - Superadmin in specific tenant: tenant name
 * - Regular users: their tenant name (fetched from /tenants/current endpoint)
 */
export const TenantIndicator: React.FC = () => {
  const { user } = useAuth();
  const { selectedTenantId, isInGlobalView } = useTenantContext();
  const api = useMemo(() => new Api(), []);
  const [tenantName, setTenantName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const isSuperAdmin = !!user?.is_superadmin;

  const effectiveTenantId = isSuperAdmin ? selectedTenantId : user?.tenant_id ?? null;
  const effectiveGlobalView = isSuperAdmin ? isInGlobalView : false;

  // Fetch tenant name using /tenants/current endpoint (works for all users)
  // For superadmins: fetches the currently selected tenant
  // For regular users: fetches their own tenant (ignores X-Tenant-ID header since we don't send it for them)
  useEffect(() => {
    if (effectiveGlobalView || effectiveTenantId === null) {
      setTenantName('');
      return;
    }

    const fetchTenantName = async () => {
      try {
        setLoading(true);
        const response = await api.get<Tenant>('/tenants/current');
        setTenantName(response?.name ?? `Tenant #${effectiveTenantId}`);
      } catch (error) {
        // Fallback to ID if fetch fails
        setTenantName(`Tenant #${effectiveTenantId}`);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantName();
  }, [api, effectiveTenantId, effectiveGlobalView, selectedTenantId, isInGlobalView]); // Force refetch when selectedTenantId changes

  // Indicador de visão global (apenas para super admin)
  if (effectiveGlobalView) {
    return (
      <Tooltip title="Visualizando dados agregados de todos os tenants">
        <Tag
          icon={<GlobalOutlined />}
          color="geekblue"
          className="cursor-default"
          style={{ marginRight: 0 }}
        >
          Visão Global
        </Tag>
      </Tooltip>
    );
  }

  // Indicador de tenant específico
  if (effectiveTenantId) {
    const displayText = tenantName || `Tenant #${effectiveTenantId}`;

    return (
      <Tooltip title={`Selecionado: ${displayText}`}>
        <Tag
          icon={<TeamOutlined />}
          color="cyan"
          className="cursor-default"
          style={{ marginRight: 0 }}
        >
          {loading ? 'Carregando...' : displayText}
        </Tag>
      </Tooltip>
    );
  }

  // Fallback se não houver tenant (não esperado no fluxo normal)
  return null;
};

export default TenantIndicator;